(() => {
  const mobileQuery = window.matchMedia('(max-width: 767.98px)');
  const filterSelector = '.filter-container--burvonti-mobile';
  const quickViewSelector = '[data-bvt-plp-quick-view]';
  const blockingOverlaySelector = `.modal[open], .drawer[open], ${quickViewSelector}.is-open`;
  const body = document.body;
  const productCache = new Map();
  const productPageCache = new Map();
  const mobileNavigationPlacements = new Map();
  const shapeTokens = new Set(['round', 'oval', 'cushion', 'pear', 'princess', 'emerald', 'marquise', 'asscher', 'radiant', 'heart']);
  const metalTokens = ['14k-white-gold', '14k-yellow-gold', '14k-rose-gold', '18k-white-gold', '18k-yellow-gold', 'platinum'];
  let mobileStyleNavigation = null;
  let lastSortTrigger = null;
  let lastFilterTrigger = null;
  let lastQuickViewTrigger = null;
  let quickViewAbortController = null;
  let feedbackTimer = null;

  const routesRoot = () => (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');

  body.classList.toggle('bvt-mobile-filter-available', Boolean(document.querySelector('.bvt-mobile-filter-trigger')));

  const currentCollectionHandle = () => {
    const parts = window.location.pathname.split('/').filter(Boolean);
    return parts[0] === 'collections' && parts[1] ? parts[1].toLowerCase() : '';
  };

  const currentMetalToken = () => {
    const handle = currentCollectionHandle();
    return metalTokens.find((token) => handle.startsWith(`${token}-`) || handle.endsWith(`-${token}`)) || '';
  };

  const currentShapeSelection = () => {
    const pathToken = currentShapeToken();
    if (pathToken) return pathToken;
    return document.querySelector('.section-bvt-shape-navigation [data-bvt-shape-token][aria-current="page"]')?.dataset.bvtShapeToken?.toLowerCase() || '';
  };

  const styleBaseHandle = (sourceLink) => sourceLink.dataset.bvtStyleBaseHandle
    || new URL(sourceLink.href, window.location.origin).pathname.split('/').filter(Boolean)[1]
    || '';

  const styleTargetUrl = (sourceLink, preserveAxes = true) => {
    const targetUrl = new URL(sourceLink.href, window.location.origin);
    const targetHandle = styleBaseHandle(sourceLink) || 'engagement-rings';
    const metal = preserveAxes ? currentMetalToken() : '';
    const shape = preserveAxes ? currentShapeSelection() : '';
    const availableShapes = new Set((sourceLink.dataset.bvtStyleAvailableShapes || '').split('|').filter(Boolean));
    let resolvedHandle = targetHandle;

    if (metal) {
      resolvedHandle = targetHandle === 'engagement-rings'
        ? `${metal}-engagement-rings`
        : `${targetHandle}-${metal}`;
    }

    targetUrl.pathname = `${routesRoot()}collections/${resolvedHandle}`.replace(/\/+/g, '/');
    if (shape && availableShapes.has(shape)) {
      targetUrl.pathname = `${targetUrl.pathname.replace(/\/$/, '')}/${shape}`;
    }
    targetUrl.search = '';
    targetUrl.hash = '';
    return targetUrl.href;
  };

  const createMobileStyleNavigation = () => {
    const source = document.querySelector('[data-bvt-card-profile="engagement-style"]');
    if (!source) return null;
    const sourceLinks = [...source.querySelectorAll('[data-bvt-style-target], .bvt-related-collection-cards__card[href]')]
      .filter((link) => styleBaseHandle(link) && link.textContent.trim());
    if (sourceLinks.length < 2) return null;

    const allStylesSource = sourceLinks.find((link) => styleBaseHandle(link) === 'engagement-rings') || sourceLinks[0];
    const activeSource = sourceLinks.find((link) => link.getAttribute('aria-current') === 'page') || allStylesSource;
    const navigation = document.createElement('section');
    navigation.className = 'bvt-mobile-style-navigation';
    navigation.dataset.bvtMobileStyleNavigation = '';
    navigation.innerHTML = `
      <div class="bvt-mobile-style-navigation__header">
        <h2 class="bvt-mobile-style-navigation__heading">Style</h2>
        <a class="bvt-mobile-style-navigation__all" data-bvt-style-clear>
          <span data-bvt-style-state></span>
        </a>
      </div>
      <div class="bvt-mobile-style-navigation__options" role="list"></div>`;

    const clearLink = navigation.querySelector('[data-bvt-style-clear]');
    clearLink.href = styleTargetUrl(allStylesSource, true);
    clearLink.dataset.bvtStyleResetAll = styleTargetUrl(allStylesSource, false);
    navigation.querySelector('[data-bvt-style-state]').textContent = activeSource === allStylesSource
      ? 'All'
      : activeSource.textContent.trim();

    const options = navigation.querySelector('.bvt-mobile-style-navigation__options');
    sourceLinks.forEach((sourceLink) => {
      const option = document.createElement('a');
      const isActive = sourceLink === activeSource;
      option.className = `bvt-mobile-style-navigation__option${isActive ? ' is-active' : ''}`;
      option.href = styleTargetUrl(sourceLink, true);
      option.dataset.bvtStyleOption = '';
      option.dataset.bvtStyleLabel = sourceLink.textContent.trim();
      option.dataset.bvtStyleAll = sourceLink === allStylesSource ? 'true' : 'false';
      option.setAttribute('role', 'listitem');
      option.textContent = sourceLink.textContent.trim();
      if (isActive) option.setAttribute('aria-current', 'page');
      options.appendChild(option);
    });
    return navigation;
  };

  const syncMobileNavigation = () => {
    const slot = document.querySelector('[data-bvt-mobile-filter-navigation]');
    // AJAX replaces the filter slot; retain moved nodes via their live placeholders.
    const sources = [...new Set([
      ...document.querySelectorAll('.section-bvt-shape-navigation, .section-bvt-metal-navigation'),
      ...Array.from(mobileNavigationPlacements.keys())
        .filter((source) => mobileNavigationPlacements.get(source)?.isConnected)
    ])].filter((source) => {
        const selector = source.matches('.section-bvt-shape-navigation')
          ? '[data-bvt-shape-token]'
          : '[data-bvt-metal-target], .bvt-metal-navigation__chip';
        const hasOptions = Boolean(source.querySelector(selector));
        source.toggleAttribute('hidden', !hasOptions);
        return hasOptions;
      });

    sources.forEach((source) => {
      if (mobileNavigationPlacements.has(source)) return;
      const placeholder = document.createComment('Burvonti collection filter navigation');
      source.parentNode.insertBefore(placeholder, source);
      mobileNavigationPlacements.set(source, placeholder);
    });

    if (mobileQuery.matches && slot) {
      if (!mobileStyleNavigation) mobileStyleNavigation = createMobileStyleNavigation();
      if (mobileStyleNavigation) slot.appendChild(mobileStyleNavigation);
      sources.forEach((source) => slot.appendChild(source));
      body.classList.add('bvt-mobile-navigation-mounted');
    } else {
      if (mobileStyleNavigation) mobileStyleNavigation.remove();
      mobileNavigationPlacements.forEach((placeholder, source) => {
        if (placeholder.parentNode) placeholder.parentNode.insertBefore(source, placeholder.nextSibling);
      });
      body.classList.remove('bvt-mobile-navigation-mounted');
    }
  };

  const formatMoney = (cents, includeCurrencyCode = true) => {
    const amount = cents / 100;
    const format = window.theme && window.theme.settings && window.theme.settings.moneyWithCurrencyFormat;
    if (format && format.includes('{{amount')) {
      const formatted = format.replace(/\{\{\s*(amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator)?)\s*\}\}/, (_match, token) => {
        const noDecimals = token.includes('no_decimals');
        const useCommaDecimal = token.includes('comma_separator');
        return amount.toLocaleString(useCommaDecimal ? 'de-DE' : 'en-US', {
          minimumFractionDigits: noDecimals ? 0 : 2,
          maximumFractionDigits: noDecimals ? 0 : 2
        });
      });
      return includeCurrencyCode ? formatted : formatted.replace(/^\s*[A-Z]{3}\s*/u, '').replace(/\s*[A-Z]{3}\s*$/u, '');
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const productImageUrl = (product) => {
    const images = (product.images || []).map((image) => (typeof image === 'string' ? image : image.src)).filter(Boolean);
    const mainWhite = images.find((image) => /main[_ -]?white/i.test(image));
    const featured = typeof product.featured_image === 'string'
      ? product.featured_image
      : product.featured_image && product.featured_image.src;
    return mainWhite || featured || images[0] || '';
  };

  const sizedImageUrl = (rawUrl, width) => {
    const imageUrl = new URL(rawUrl, window.location.origin);
    imageUrl.searchParams.set('width', width);
    return imageUrl.toString();
  };

  const fetchProduct = (handle) => {
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(`${routesRoot()}products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json' }
        }).then((response) => {
          if (!response.ok) throw new Error(`Product preview failed: ${response.status}`);
          return response.json();
        })
      );
    }
    return productCache.get(handle);
  };

  const updateCardActions = (card, product) => {
    const actions = card.querySelector('[data-bvt-card-actions]');
    if (!actions) return;
    const variants = product.variants || [];
    const requiresProductOptions = /\b(ring|band)\b/i.test(`${product.type || ''} ${product.title || ''}`)
      || (product.selling_plan_groups || []).length > 0;
    const directVariant = variants.length === 1 && variants[0].available && !requiresProductOptions ? variants[0] : null;
    const productUrl = `${routesRoot()}products/${product.handle}`;
    const purchase = actions.querySelector('[data-bvt-card-purchase]');

    actions.dataset.productHandle = product.handle;
    actions.dataset.productUrl = productUrl;
    actions.dataset.productTitle = product.title;
    actions.dataset.variantCount = String(variants.length);
    actions.dataset.variantId = directVariant ? String(directVariant.id) : '';
    actions.dataset.directAdd = directVariant ? 'true' : 'false';
    actions.dataset.productAvailable = product.available ? 'true' : 'false';

    if (purchase) {
      purchase.disabled = !product.available;
      purchase.textContent = !product.available ? 'Sold out' : (directVariant ? 'Quick add' : 'Choose options');
    }
  };

  const updateCardFromProduct = (card, product, selectedControl) => {
    const productUrl = `${routesRoot()}products/${product.handle}`;
    const imageUrl = productImageUrl(product);

    card.dataset.productId = product.id;
    card.querySelectorAll('a.product-link, a.quickbuy-toggle').forEach((link) => {
      link.href = productUrl;
    });

    if (imageUrl) {
      const image = card.querySelector('.product-block__image--primary img');
      if (image) {
        image.src = sizedImageUrl(imageUrl, 540);
        image.srcset = `${sizedImageUrl(imageUrl, 240)} 240w, ${sizedImageUrl(imageUrl, 360)} 360w, ${sizedImageUrl(imageUrl, 540)} 540w`;
        image.alt = product.title;
      }
    }

    const prices = (product.variants || []).map((variant) => variant.price).filter((price) => Number.isFinite(price));
    const price = prices.length ? Math.min(...prices) : product.price;
    const priceNode = card.querySelector('.product-price .price__current > span:last-child');
    if (priceNode && Number.isFinite(price)) {
      const includeCurrencyCode = /\b[A-Z]{3}\b/u.test(priceNode.textContent);
      priceNode.textContent = formatMoney(price, includeCurrencyCode);
    }

    const selectedGroup = selectedControl.closest('[data-bvt-card-preview-group]');
    if (selectedGroup) {
      selectedGroup.querySelectorAll('[data-bvt-card-sibling-target]').forEach((control) => {
        const isActive = control === selectedControl;
        control.classList.toggle('is-active', isActive);
        if (isActive) control.setAttribute('aria-current', 'true');
        else control.removeAttribute('aria-current');
      });
    }

    updateCardActions(card, product);
    const status = card.querySelector('[data-bvt-card-preview-status]');
    if (status) status.textContent = `${product.title} selected`;
  };

  const resultCount = () => {
    const source = document.querySelector('.filters-adjacent[data-bvt-result-count]')
      || document.querySelector('[data-bvt-result-count]');
    const count = source ? Number.parseInt(source.dataset.bvtResultCount, 10) : 0;
    return Number.isFinite(count) ? count : 0;
  };

  const baseCollectionUrl = () => {
    const parts = window.location.pathname.split('/').filter(Boolean);
    if (parts[0] !== 'collections' || !parts[1]) return window.location.pathname;
    const handle = parts[1].toLowerCase();
    const prefixes = ['14k-white-gold-', '14k-yellow-gold-', '14k-rose-gold-', '18k-white-gold-', '18k-yellow-gold-', 'platinum-'];
    const suffixes = ['-14k-white-gold', '-14k-yellow-gold', '-14k-rose-gold', '-18k-white-gold', '-18k-yellow-gold', '-platinum'];
    const prefix = prefixes.find((token) => handle.startsWith(token));
    const suffix = suffixes.find((token) => handle.endsWith(token));
    const baseHandle = prefix ? handle.slice(prefix.length) : (suffix ? handle.slice(0, -suffix.length) : handle);
    return new URL(`${routesRoot()}collections/${baseHandle}`, window.location.origin).href;
  };

  const currentShapeToken = () => {
    const parts = window.location.pathname.split('/').filter(Boolean);
    if (parts[0] !== 'collections' || !parts[2]) return '';
    const token = parts[2].toLowerCase();
    return shapeTokens.has(token) ? token : '';
  };

  const selectedFilterEntries = () => {
    const entries = [];
    document.querySelectorAll(`${filterSelector} .filter-group__applied-item`).forEach((link) => {
      const label = link.querySelector('.filter-group__applied-item__text')?.textContent.trim();
      if (label) entries.push({ axis: 'native', label, url: link.href });
    });

    const style = document.querySelector('[data-bvt-style-option].is-active:not([data-bvt-style-all="true"])');
    const styleClear = document.querySelector('[data-bvt-style-clear]');
    if (style && styleClear) {
      entries.push({ axis: 'style', label: style.dataset.bvtStyleLabel || style.textContent.trim(), url: styleClear.href });
    }

    const shape = document.querySelector('.bvt-shape-navigation__chip--active[aria-current="page"]');
    const shapeClear = document.querySelector('[data-bvt-shape-clear]');
    const shapeToken = currentShapeToken();
    if ((shape || shapeToken) && shapeClear) {
      const fallbackLabel = shapeToken ? `${shapeToken.charAt(0).toUpperCase()}${shapeToken.slice(1)}` : 'Shape';
      entries.push({ axis: 'shape', label: shape?.getAttribute('aria-label') || fallbackLabel, url: shapeClear.href });
    }

    const metal = document.querySelector('.bvt-metal-navigation__chip--active[aria-current="page"]');
    if (metal) {
      const removeMetalUrl = new URL(baseCollectionUrl(), window.location.origin);
      if (shapeToken) {
        removeMetalUrl.pathname = `${removeMetalUrl.pathname.replace(/\/$/, '')}/${shapeToken}`;
      }
      entries.push({ axis: 'metal', label: metal.getAttribute('aria-label') || 'Metal', url: removeMetalUrl.href });
    }
    return entries;
  };

  const clearAllUrl = () => {
    const styleClear = document.querySelector('[data-bvt-style-clear]');
    if (styleClear?.dataset.bvtStyleResetAll) return styleClear.dataset.bvtStyleResetAll;
    const activeMetal = document.querySelector('.bvt-metal-navigation__chip--active[aria-current="page"]');
    const shapeClear = document.querySelector('[data-bvt-shape-clear]');
    return activeMetal ? baseCollectionUrl() : ((shapeClear && shapeClear.href) || window.location.pathname);
  };

  const syncFilterFeedback = () => {
    feedbackTimer = null;
    const entries = selectedFilterEntries();
    const count = entries.length;
    const countResult = resultCount();
    const allClearUrl = clearAllUrl();

    document.querySelectorAll('[data-bvt-filter-count]').forEach((node) => {
      const text = `(${count})`;
      if (node.textContent !== text) node.textContent = text;
      if (node.hidden === (count > 0)) node.hidden = count === 0;
    });

    document.querySelectorAll('[data-bvt-filter-apply]').forEach((button) => {
      const nextText = `View ${countResult} ${countResult === 1 ? 'result' : 'results'}`;
      if (button.textContent.trim() !== nextText) button.textContent = nextText;
    });

    document.querySelectorAll('[data-bvt-clear-all], [data-bvt-filter-clear]').forEach((link) => {
      if (link.href !== allClearUrl) link.href = allClearUrl;
    });

    document.querySelectorAll('[data-bvt-active-filters]').forEach((container) => {
      const list = container.querySelector('[data-bvt-active-filter-list]');
      const signature = entries.map((entry) => `${entry.axis}|${entry.label}|${entry.url}`).join('||');
      if (list && list.dataset.bvtSignature !== signature) {
        list.innerHTML = '';
        entries.forEach((entry) => {
          const chip = document.createElement('a');
          chip.className = 'bvt-active-filters__chip';
          chip.href = entry.url;
          chip.dataset.bvtFilterAxis = entry.axis;
          chip.textContent = entry.label;
          const remove = document.createElement('span');
          remove.setAttribute('aria-hidden', 'true');
          remove.textContent = '×';
          chip.appendChild(remove);
          list.appendChild(chip);
        });
        list.dataset.bvtSignature = signature;
      }
      if (container.hidden === (count > 0)) container.hidden = count === 0;
    });

    document.querySelectorAll('[data-bvt-low-result]').forEach((recovery) => {
      const shouldShow = count > 0 && countResult > 0 && countResult <= 4;
      if (recovery.hidden === shouldShow) recovery.hidden = !shouldShow;
      const label = recovery.querySelector('span');
      if (label) label.textContent = `Only ${countResult} ${countResult === 1 ? 'style matches' : 'styles match'}.`;
    });
  };

  const scheduleFilterFeedback = () => {
    if (feedbackTimer) window.clearTimeout(feedbackTimer);
    feedbackTimer = window.setTimeout(syncFilterFeedback, 60);
  };

  const syncFilterState = (container) => {
    const isOpen = mobileQuery.matches && container.classList.contains('filter-container--show-filters-mobile');
    body.classList.toggle('bvt-mobile-filter-open', isOpen);
    document.querySelectorAll('.bvt-mobile-filter-trigger').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  };

  const initialiseFilter = (container) => {
    if (container.dataset.bvtMobileObserved === 'true') return;
    container.dataset.bvtMobileObserved = 'true';
    const observer = new MutationObserver(() => {
      syncFilterState(container);
      scheduleFilterFeedback();
    });
    observer.observe(container, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });
    syncFilterState(container);
  };

  const setFilterOpen = (container, shouldOpen, restoreFocus = false) => {
    if (!container || !mobileQuery.matches) return;
    container.classList.toggle('filter-container--show-filters-mobile', shouldOpen);
    container.classList.remove('filter-container--show-filters-desktop');
    syncFilterState(container);

    if (shouldOpen) {
      const closeButton = container.querySelector('.filters__close[data-toggle-filters]');
      if (closeButton) closeButton.focus();
    } else if (restoreFocus && lastFilterTrigger) {
      lastFilterTrigger.focus();
    }
  };

  const closeSort = (restoreFocus = true) => {
    const sheet = document.querySelector('[data-bvt-sort-sheet].is-open');
    if (!sheet) return;
    sheet.classList.remove('is-open');
    sheet.setAttribute('aria-hidden', 'true');
    body.classList.remove('bvt-mobile-sort-open');
    document.querySelectorAll('[data-bvt-sort-open]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    if (restoreFocus && lastSortTrigger) lastSortTrigger.focus();
  };

  const closeQuickView = (restoreFocus = true) => {
    const quickView = document.querySelector(`${quickViewSelector}.is-open`);
    if (!quickView) return;
    if (quickViewAbortController) quickViewAbortController.abort('Quick view closed');
    quickViewAbortController = null;
    quickView.classList.remove('is-open', 'is-loading');
    quickView.setAttribute('aria-hidden', 'true');
    body.classList.remove('bvt-mobile-quick-view-open');
    const content = quickView.querySelector('[data-bvt-quick-view-content]');
    if (content) content.innerHTML = '';
    if (restoreFocus && lastQuickViewTrigger && document.contains(lastQuickViewTrigger)) lastQuickViewTrigger.focus();
  };

  const closePlpOverlays = () => {
    closeSort(false);
    const openFilterContainer = document.querySelector(`${filterSelector}.filter-container--show-filters-mobile`);
    setFilterOpen(openFilterContainer, false, false);
    closeQuickView(false);
  };

  const openFilter = (trigger) => {
    if (!mobileQuery.matches || document.querySelector(blockingOverlaySelector)) return;
    closeSort(false);
    const targetId = trigger.getAttribute('aria-controls');
    const container = targetId ? document.getElementById(targetId) : document.querySelector(filterSelector);
    if (!container) return;
    lastFilterTrigger = trigger;
    setFilterOpen(container, true);
  };

  const openSort = (trigger) => {
    const targetId = trigger.getAttribute('aria-controls');
    const sheet = targetId ? document.getElementById(targetId) : null;
    if (!sheet || !mobileQuery.matches || document.querySelector(blockingOverlaySelector)) return;
    const openFilterContainer = document.querySelector(`${filterSelector}.filter-container--show-filters-mobile`);
    setFilterOpen(openFilterContainer, false, false);
    lastSortTrigger = trigger;
    sheet.classList.add('is-open');
    sheet.setAttribute('aria-hidden', 'false');
    body.classList.add('bvt-mobile-sort-open');
    trigger.setAttribute('aria-expanded', 'true');
    const closeButton = sheet.querySelector('.bvt-mobile-sort-sheet__close');
    if (closeButton) window.setTimeout(() => closeButton.focus(), 50);
  };

  const addQuickViewResources = (template) => {
    template.content.querySelectorAll(':is(.section-main-product, .section-size-guide) link[rel="stylesheet"]').forEach((link) => {
      if (!document.querySelector(`link[rel="stylesheet"][href="${link.getAttribute('href')}"]`)) {
        document.head.insertAdjacentHTML('beforeend', link.outerHTML);
      }
    });
    template.content.querySelectorAll(':is(.section-main-product, .section-size-guide) script[src]').forEach((script) => {
      if (!document.querySelector(`script[src="${script.getAttribute('src')}"]`)) {
        const resource = document.createElement('script');
        resource.src = script.getAttribute('src');
        resource.defer = true;
        document.head.appendChild(resource);
      }
    });
  };

  const quickViewContent = (html, productUrl) => {
    const template = document.createElement('template');
    template.innerHTML = html;
    const source = template.content.querySelector('.quickbuy-content');
    if (!source) throw new Error('Quick view content not found');
    const detail = source.cloneNode(true);

    template.content.querySelectorAll('.section-size-guide').forEach((section) => {
      if (section.querySelector('.modal--for-size-guide')) {
        detail.insertAdjacentElement('beforeend', section.cloneNode(true));
      } else {
        const sourceTemplate = section.querySelector('[data-modal-template]');
        if (!sourceTemplate) return;
        const sizeGuide = sourceTemplate.content.querySelector('size-guide')?.cloneNode(true);
        const content = section.querySelector('.size-guide__content');
        if (sizeGuide && content) {
          sizeGuide.querySelector('.modal__content')?.appendChild(content.cloneNode(true));
          detail.insertAdjacentElement('beforeend', sizeGuide);
        }
      }
    });

    if (window.theme && window.theme.quickBuy) window.theme.quickBuy.convertToQuickBuyContent(detail, productUrl);
    addQuickViewResources(template);
    detail.classList.add('bvt-plp-quick-view__product');
    return detail;
  };

  const openQuickView = (trigger, actions) => {
    const quickView = document.querySelector(quickViewSelector);
    const productUrl = actions && actions.dataset.productUrl;
    if (!quickView || !productUrl || !mobileQuery.matches) return;

    closePlpOverlays();
    lastQuickViewTrigger = trigger;
    quickView.classList.add('is-open', 'is-loading');
    quickView.setAttribute('aria-hidden', 'false');
    body.classList.add('bvt-mobile-quick-view-open');

    const heading = quickView.querySelector('[data-bvt-quick-view-heading]');
    const content = quickView.querySelector('[data-bvt-quick-view-content]');
    const status = quickView.querySelector('[data-bvt-quick-view-status]');
    if (heading) heading.textContent = 'Quick view';
    if (content) {
      content.dataset.productUrl = productUrl;
      content.innerHTML = '<div class="loading-spinner" aria-hidden="true"></div>';
    }
    if (status) status.textContent = 'Loading product options';
    quickView.querySelector('[data-bvt-quick-view-close]')?.focus();

    if (quickViewAbortController) quickViewAbortController.abort('New quick view requested');
    quickViewAbortController = new AbortController();
    if (!productPageCache.has(productUrl)) {
      productPageCache.set(productUrl, fetch(productUrl, { signal: quickViewAbortController.signal }).then((response) => {
        if (!response.ok) throw new Error(`Quick view failed: ${response.status}`);
        return response.text();
      }).catch((error) => {
        productPageCache.delete(productUrl);
        throw error;
      }));
    }

    productPageCache.get(productUrl)
      .then((html) => {
        if (!quickView.classList.contains('is-open')) return;
        const detail = quickViewContent(html, productUrl);
        content.innerHTML = '';
        content.appendChild(detail);
        quickView.classList.remove('is-loading');
        if (status) status.textContent = 'Product options loaded';
        quickView.dispatchEvent(new CustomEvent('on:quickbuy:after-open', { bubbles: true }));
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        quickView.classList.remove('is-loading');
        content.innerHTML = '';
        const message = document.createElement('p');
        message.textContent = 'Quick view could not load.';
        const link = document.createElement('a');
        link.className = 'btn btn--secondary';
        link.href = productUrl;
        link.textContent = 'View full details';
        content.append(message, link);
        if (status) status.textContent = 'Quick view could not load';
      });
  };

  const directAdd = async (button, actions) => {
    const variantId = actions.dataset.variantId;
    if (!variantId || button.disabled || button.classList.contains('is-loading')) return;
    const status = actions.querySelector('[data-bvt-card-action-status]');
    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');
    if (status) status.textContent = 'Adding to cart';

    try {
      const productUrl = actions.dataset.productUrl;
      if (!productPageCache.has(productUrl)) {
        productPageCache.set(productUrl, fetch(productUrl).then((response) => {
          if (!response.ok) throw new Error(`Product form check failed: ${response.status}`);
          return response.text();
        }).catch((error) => {
          productPageCache.delete(productUrl);
          throw error;
        }));
      }
      const productHtml = await productPageCache.get(productUrl);
      const template = document.createElement('template');
      template.innerHTML = productHtml;
      const productForm = template.content.querySelector('.quickbuy-content .js-product-form');
      const requiredProductFields = productForm
        ? [...productForm.querySelectorAll('[required]')].filter((field) => field.name !== 'id' && field.type !== 'hidden')
        : [];
      if (!productForm || requiredProductFields.length > 0) {
        actions.dataset.directAdd = 'false';
        actions.dataset.variantId = '';
        button.textContent = 'Choose options';
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
        if (status) status.textContent = 'Choose the required product options';
        openQuickView(button, actions);
        return;
      }
    } catch (error) {
      actions.dataset.directAdd = 'false';
      actions.dataset.variantId = '';
      button.textContent = 'Choose options';
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
      if (status) status.textContent = 'Choose the required product options';
      openQuickView(button, actions);
      return;
    }

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    const sectionsToUpdate = ['page-header', 'cart-drawer'].map((selector) => document.querySelector(selector)).filter(Boolean);
    const sectionIds = sectionsToUpdate.map((section) => section.dataset.sectionId);
    formData.append('sections_url', window.location.pathname);
    formData.append('sections', sectionIds.join(','));

    try {
      const response = await fetch(window.theme && window.theme.routes ? window.theme.routes.cartAdd : `${routesRoot()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this item');

      if (data.sections && sectionsToUpdate.length === Object.keys(data.sections).length) {
        sectionsToUpdate.forEach((section) => section.updateFromCartChange(data.sections[section.dataset.sectionId]));
      } else {
        button.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      }

      const cartDrawer = document.querySelector('.js-cart-drawer');
      if (cartDrawer && window.theme.settings.afterAddToCart === 'drawer') cartDrawer.open(button);
      else if (window.theme.settings.afterAddToCart === 'page') window.location.assign(window.theme.routes.cart);

      button.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: data.variant_id } }));
      if (status) status.textContent = `${actions.dataset.productTitle || 'Item'} added to cart`;
      button.textContent = 'Added';
      window.setTimeout(() => {
        button.textContent = 'Quick add';
      }, 1800);
    } catch (error) {
      if (status) status.textContent = error.message || 'Unable to add this item';
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
    }
  };

  const blockingOverlayObserver = new MutationObserver((mutations) => {
    if (mutations.some(({ target }) => target.matches?.('.modal[open], .drawer[open]'))) closePlpOverlays();
  });
  document.querySelectorAll('.modal, .drawer').forEach((overlay) => {
    blockingOverlayObserver.observe(overlay, { attributes: true, attributeFilter: ['open'] });
  });
  document.addEventListener('on:collection:updated', () => {
    syncMobileNavigation();
    document.querySelectorAll(filterSelector).forEach(syncFilterState);
    scheduleFilterFeedback();
  });
  document.addEventListener('on:cart-drawer:before-open', closePlpOverlays);

  document.querySelectorAll(filterSelector).forEach(initialiseFilter);
  syncMobileNavigation();
  scheduleFilterFeedback();
  body.dataset.bvtMobilePlpReady = 'true';

  document.addEventListener('click', (event) => {
    const filterTrigger = event.target.closest('.bvt-mobile-filter-trigger');
    if (filterTrigger && mobileQuery.matches) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      openFilter(filterTrigger);
      return;
    }

    const closeControl = event.target.closest(`${filterSelector} [data-toggle-filters]`);
    if (closeControl && mobileQuery.matches) {
      const container = closeControl.closest(filterSelector);
      if (container && container.classList.contains('filter-container--show-filters-mobile')) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        setFilterOpen(container, false, true);
      }
    }
  }, true);

  document.addEventListener('click', (event) => {
    const siblingTarget = event.target.closest('[data-bvt-card-sibling-target]');
    if (siblingTarget) {
      event.preventDefault();
      const card = siblingTarget.closest('product-block');
      const handle = siblingTarget.dataset.targetHandle;
      if (!card || !handle || card.classList.contains('bvt-card-preview-loading')) return;
      card.classList.add('bvt-card-preview-loading');
      fetchProduct(handle)
        .then((product) => updateCardFromProduct(card, product, siblingTarget))
        .catch(() => window.location.assign(siblingTarget.href))
        .finally(() => card.classList.remove('bvt-card-preview-loading'));
      return;
    }

    const quickViewOpen = event.target.closest('[data-bvt-quick-view-open]');
    if (quickViewOpen && mobileQuery.matches) {
      event.preventDefault();
      openQuickView(quickViewOpen, quickViewOpen.closest('[data-bvt-card-actions]'));
      return;
    }

    const purchase = event.target.closest('[data-bvt-card-purchase]');
    if (purchase && mobileQuery.matches) {
      event.preventDefault();
      const actions = purchase.closest('[data-bvt-card-actions]');
      if (!actions || actions.dataset.productAvailable !== 'true') return;
      if (actions.dataset.directAdd === 'true') directAdd(purchase, actions);
      else openQuickView(purchase, actions);
      return;
    }

    if (event.target.closest('[data-bvt-quick-view-close]')) {
      event.preventDefault();
      closeQuickView();
      return;
    }

    const sortOpen = event.target.closest('[data-bvt-sort-open]');
    if (sortOpen) {
      event.preventDefault();
      openSort(sortOpen);
      return;
    }

    if (event.target.closest('[data-bvt-sort-close]')) {
      event.preventDefault();
      closeSort();
      return;
    }

    if (event.target.closest('.bvt-mobile-sort-sheet__option')) {
      closeSort(false);
      return;
    }

    const applyButton = event.target.closest('[data-bvt-filter-apply]');
    if (applyButton) {
      event.preventDefault();
      setFilterOpen(applyButton.closest(filterSelector), false, true);
    }
  });

  document.addEventListener('keydown', (event) => {
    const quickView = document.querySelector(`${quickViewSelector}.is-open`);
    if (event.key === 'Tab' && quickView) {
      const focusable = [...quickView.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((element) => element.offsetParent !== null);
      if (focusable.length > 0) {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
      return;
    }
    if (event.key !== 'Escape') return;
    if (quickView) {
      closeQuickView();
      return;
    }
    if (document.querySelector('[data-bvt-sort-sheet].is-open')) {
      closeSort();
      return;
    }
    setFilterOpen(document.querySelector(`${filterSelector}.filter-container--show-filters-mobile`), false, true);
  });

  window.addEventListener('pageshow', scheduleFilterFeedback);
  window.addEventListener('popstate', scheduleFilterFeedback);

  const handleBreakpoint = () => {
    syncMobileNavigation();
    if (!mobileQuery.matches) {
      closeSort(false);
      closeQuickView(false);
      body.classList.remove('bvt-mobile-filter-open');
      document.querySelectorAll(`${filterSelector}.filter-container--show-filters-mobile`).forEach((container) => {
        container.classList.remove('filter-container--show-filters-mobile');
      });
    }
    document.querySelectorAll(filterSelector).forEach(syncFilterState);
    scheduleFilterFeedback();
  };

  if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', handleBreakpoint);
  else mobileQuery.addListener(handleBreakpoint);
})();
